/**
 * Globe setup - Mapbox GL JS globe projection.
 * Handles map init, fog, style switching.
 */
import mapboxgl from 'mapbox-gl';

/** Default center on Africa */
const AFRICA_CENTER: [number, number] = [20, 5];
const AFRICA_ZOOM = window.innerWidth < 768 ? 1.8 : 2.5;

/** Zoom constraints */
const MIN_ZOOM = 1.5;
const MAX_ZOOM = 16;  // deep zoom for airport/airspace detail

/** Available base map styles */
export const MAP_STYLES = {
  dark:      { id: 'dark',      label: 'Dark',       url: 'mapbox://styles/mapbox/dark-v11',              icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="14" height="14"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>' },
  terrain:   { id: 'terrain',   label: 'Light',      url: 'mapbox://styles/mapbox/outdoors-v12',          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="14" height="14"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>' },
  satellite: { id: 'satellite', label: 'Satellite',  url: 'mapbox://styles/mapbox/satellite-streets-v12', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="14" height="14"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>' },
} as const;

export type StyleId = keyof typeof MAP_STYLES;

let currentStyleId: StyleId = 'dark';

export function getCurrentStyleId(): StyleId {
  return currentStyleId;
}

export function initGlobe(container: string, token: string): mapboxgl.Map {
  mapboxgl.accessToken = token;

  const map = new mapboxgl.Map({
    container,
    style: MAP_STYLES.dark.url,
    center: AFRICA_CENTER,
    zoom: AFRICA_ZOOM,
    minZoom: MIN_ZOOM,
    maxZoom: MAX_ZOOM,
    projection: 'globe',
    antialias: true,
    fadeDuration: 0,
  });

  // Navigation controls (zoom buttons, compass)
  map.addControl(new mapboxgl.NavigationControl({ showCompass: true }), 'bottom-right');

  return map;
}

/** Add DEM source + terrain for elevation queries.
 *  Must be called on every style.load (style switches clear sources).
 */
export function addTerrain(map: mapboxgl.Map) {
  if (!map.getSource('mapbox-dem')) {
    map.addSource('mapbox-dem', {
      type: 'raster-dem',
      url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
      tileSize: 512,
      maxzoom: 14,
    });
  }
  map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.0 });
}

/** Apply atmosphere/fog.
 *  Dark mode → deep-space look with a vivid blue halo at the globe edge.
 *  Other styles → no fog (outdoors/satellite imagery looks better without it).
 */
export function applyFog(map: mapboxgl.Map) {
  if (currentStyleId === 'dark') {
    // Thin blue halo behind the globe edge — minimal bleed onto the earth surface
    map.setFog({
      color:            'rgb(10,  30, 100)',   // dim blue at ground — keeps halo, not earth-glow
      'high-color':     'rgb(30,  70, 180)',   // brighter blue just outside the limb
      'horizon-blend':  0.09,                  // narrow band (~half of old 0.20) = tight halo
      'space-color':    'rgb(4,   4,  12)',    // near-black outer space
      'star-intensity': 0.50,
    });
  } else {
    // Light mode (outdoors) / Satellite — clear fog so imagery stays crisp
    map.setFog({} as any);
  }
}

/** Fly to a specific country with smooth animation */
export function flyToCountry(map: mapboxgl.Map, lng: number, lat: number, zoom = 4.5) {
  map.flyTo({
    center: [lng, lat],
    zoom,
    duration: 1800,
    essential: true,
    curve: 1.4,
  });
}

/** Reset view to Africa */
export function resetView(map: mapboxgl.Map) {
  map.flyTo({
    center: AFRICA_CENTER,
    zoom: AFRICA_ZOOM,
    duration: 1500,
    essential: true,
  });
}

/** Render and wire the style switcher pill in the bottom-right */
export function initStyleSwitcher(map: mapboxgl.Map) {
  // Remove existing switcher if present (safety guard)
  document.getElementById('style-switcher')?.remove();

  const el = document.createElement('div');
  el.id = 'style-switcher';
  el.innerHTML = Object.values(MAP_STYLES).map(s => `
    <button
      class="style-btn${s.id === currentStyleId ? ' active' : ''}"
      data-style="${s.id}"
      title="${s.label}"
    ><span class="style-btn-icon">${s.icon}</span><span>${s.label}</span></button>
  `).join('');

  document.getElementById('map')!.appendChild(el);

  el.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('.style-btn');
    if (!btn) return;
    const styleId = btn.dataset.style as StyleId;
    if (styleId === currentStyleId) return;

    currentStyleId = styleId;

    // Update active state
    el.querySelectorAll('.style-btn').forEach(b => {
      b.classList.toggle('active', (b as HTMLElement).dataset.style === styleId);
    });

    // Switch the map style — triggers style.load which re-adds layers
    map.setStyle(MAP_STYLES[styleId].url);
  });
}
